import JSZip from 'jszip'
import { filetypeinfo as getFileTypeInfo } from 'magic-bytes.js'

import { UploadedFileType } from '@/components/shared/file-uploader'
import { MAX_FILES_SIZE } from '@/utils/constants'

export const SVG_EXTENSION = '.svg'
export const IMAGE_EXTENSIONS = ['.png', '.jpg', '.jpeg', SVG_EXTENSION]
export const VIDEO_EXTENSIONS = ['.mp4', '.mpeg', '.mov']
export const WORD_EXTENSIONS = ['.doc', '.docx']
export const SHEET_EXTENSIONS = ['.xls', '.xlsx']
export const HTML_EXTENSIONS = ['.html']
export const PDF_EXTENSION = ['.pdf']

export const ALL_ALLOWED_EXTENSIONS = [
  ...IMAGE_EXTENSIONS,
  ...VIDEO_EXTENSIONS,
  ...PDF_EXTENSION,
  ...WORD_EXTENSIONS,
  ...SHEET_EXTENSIONS,
  '.hevc',
  '.wav',
  '.mp3',
  '.aac',
  '.heic',
  '.heics',
  '.pages',
  '.prores'
]

export const ALLOWED_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/heic',
  'image/heif',
  'image/svg+xml',
  'audio/aac',
  'audio/mpeg', // mp3
  'video/mpeg',
  'video/mp4',
  'video/quicktime', // mov
  'audio/wav',
  'audio/x-wav',
  'video/hevc',
  'application/msword', // doc
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // docx
  'application/vnd.ms-excel', // xls
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // xlsx
  'application/pdf',
  'application/vnd.apple.pages',
  'video/x-prores'
]

export const BYTE_SIZE = 1024 * 1024

export const isFileImageByPath = (path: string) => {
  return IMAGE_EXTENSIONS.some(ext => path.endsWith(ext))
}

export const isFileHTMLByPath = (path: string) => {
  return HTML_EXTENSIONS.some(ext => path.toLowerCase().endsWith(ext))
}

export const isFilesSizeExceeded = (
  filesSizes: number[],
  maxSize = MAX_FILES_SIZE
) => {
  return (
    filesSizes.reduce((acc, element) => acc + element, 0) > maxSize * BYTE_SIZE
  )
}

export const getFileExtensionByPath = (path: string) => {
  return path.split('.').pop()
}

const toDownloadObjectUrl = (source: UploadedFileType | File) =>
  URL.createObjectURL(source instanceof Blob ? source : source.file)

export const downloadFileByLink = (
  fileName: string,
  fileSource?: string | UploadedFileType | File
) => {
  if (fileSource) {
    const isFileLink = typeof fileSource === 'string'

    if (isFileLink) {
      const isPdf = fileSource.toLowerCase().endsWith('.pdf')
      if (isPdf) {
        window.open(fileSource, '_blank')
        return
      }
    }

    const href = isFileLink ? fileSource : toDownloadObjectUrl(fileSource)

    const link = document.createElement('a')
    link.href = href
    link.setAttribute('download', fileName)
    document.body.append(link)
    link.click()
    link.remove()

    if (!isFileLink) {
      setTimeout(() => URL.revokeObjectURL(href))
    }
  }
}

export const openUrlInNewTab = (url: string) => {
  const link = document.createElement('a')
  link.href = url
  link.target = '_blank'
  link.rel = 'noopener noreferrer'
  document.body.append(link)
  link.click()
  link.remove()
}

// Функция проверяет магические байты файла, чтобы опознать настоящее расширение.
// Сложные форматы (например, исполняемых файлов) не распознаются и функция их не пропускает
export const isFileAllowedToBeUploaded = async (file: File) => {
  const blob = file.slice(0, 100) // для наших расширений первых 100 байтов достаточно
  const buffer = await blob.arrayBuffer()
  const uint8Array = new Uint8Array(buffer)
  const predictions = getFileTypeInfo(uint8Array)

  // если тип файла не удалось распознать
  if (predictions.length === 0) {
    return false
  }

  const detectedExtension = predictions[0].extension
  const detectedMime = predictions[0].mime

  // если реальное расширение не входит в список разрешенных
  if (!ALL_ALLOWED_EXTENSIONS.includes(`.${detectedExtension}`)) {
    // Т.к. docx и xlsx основаны на формате XML (XML хранятся внутри zip-архива) они определяются как zip и требуют вскрытия
    // для дополнительной проверки
    if (detectedMime === 'application/zip') {
      try {
        const zip = new JSZip()
        const zipContent = await zip.loadAsync(file)
        const entries = Object.keys(zipContent.files)

        const hasIndexFolder = entries.some(
          name => name.startsWith('Index/') && name.includes('.iwa')
        )
        const hasMetadata = entries.some(name => name.startsWith('Metadata/'))
        const hasIwaFiles = entries.some(name => name.endsWith('.iwa'))
        const isPages = (hasIndexFolder || hasMetadata) && hasIwaFiles
        const isDocx = entries.includes('word/document.xml')
        const isXlsx = entries.includes('xl/workbook.xml')

        return isPages || isDocx || isXlsx
      } catch {
        return false
      }
    }
    return false
  }

  return !detectedMime || ALLOWED_MIME_TYPES.includes(detectedMime)
}
